/**
 * WCAG 2.1 contrast utilities for the brand/design pickers. Used to warn users
 * when a chosen color pairing is unreadable, and to drive the live indicator in
 * the per-event Design editor.
 *
 * Thresholds (WCAG 2.1 SC 1.4.3 / 1.4.6 / 1.4.11):
 *   < 3.0   fail        — not allowed (unreadable)
 *   3.0–4.5 aa-large    — not ideal: OK only for large/bold text + UI elements
 *   4.5–7.0 aa          — ideal: passes AA for all text
 *   ≥ 7.0   aaa         — best: passes AAA
 */

export type ContrastLevel = 'fail' | 'aa-large' | 'aa' | 'aaa'

function relativeLuminance(hex: string): number {
  const h = hex.replace('#', '')
  if (h.length < 6) return 0
  const ch = (i: number) => {
    const c = parseInt(h.slice(i, i + 2), 16) / 255
    return c <= 0.04045 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4)
  }
  return 0.2126 * ch(0) + 0.7152 * ch(2) + 0.0722 * ch(4)
}

/** WCAG contrast ratio between two hex colors (1–21). */
export function contrastRatio(a: string, b: string): number {
  const la = relativeLuminance(a)
  const lb = relativeLuminance(b)
  const [hi, lo] = la >= lb ? [la, lb] : [lb, la]
  return (hi + 0.05) / (lo + 0.05)
}

export function contrastLevel(ratio: number): ContrastLevel {
  if (ratio >= 7) return 'aaa'
  if (ratio >= 4.5) return 'aa'
  if (ratio >= 3) return 'aa-large'
  return 'fail'
}

/**
 * Best readable text color (black or white) for a given background, preferring
 * whichever maximizes contrast. Returns the choice + its ratio/level so callers
 * can both apply it and warn.
 */
export function bestTextOn(bgHex: string): { color: '#FFFFFF' | '#111111' | '#000000'; ratio: number; level: ContrastLevel } {
  const white = contrastRatio(bgHex, '#FFFFFF')
  const black = contrastRatio(bgHex, '#111111')
  // Near middle grey, neither white nor softened black clears normal-text AA.
  // Use true black only in that narrow band, preserving existing choices elsewhere.
  if (Math.max(white, black) < 4.5) {
    const ratio = contrastRatio(bgHex, '#000000')
    return { color: '#000000', ratio, level: contrastLevel(ratio) }
  }
  return white >= black
    ? { color: '#FFFFFF', ratio: white, level: contrastLevel(white) }
    : { color: '#111111', ratio: black, level: contrastLevel(black) }
}

/** i18n key + tone for a level, for the indicator chip. */
export function levelMeta(level: ContrastLevel): { key: string; fallback: string; tone: 'error' | 'warning' | 'success' } {
  switch (level) {
    case 'fail': return { key: 'contrast.fail', fallback: 'Ikke lesbar', tone: 'error' }
    case 'aa-large': return { key: 'contrast.aaLarge', fallback: 'Kun store elementer', tone: 'warning' }
    case 'aa': return { key: 'contrast.aa', fallback: 'God lesbarhet', tone: 'success' }
    case 'aaa': return { key: 'contrast.aaa', fallback: 'Utmerket', tone: 'success' }
  }
}
